import { useEffect, useState } from "react";
import { X, Search, Zap, AlertCircle, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { apolloSearch, getTarget } from "../lib/api";

const csv = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

const DEFAULTS = {
  titles: "", industries: "", ranges: "", locations: "", territory: "", jobTitleKeywords: "",
  jobPostedWithinDays: "60", visitorDomains: "", visitorIntentLevels: "high", visitorDays: "30",
};

export default function ApolloDiscoveryModal({ close, onImported, apolloConfigured, providerLabel = "Apollo" }) {
  const [form, setForm] = useState(DEFAULTS);
  // pre-fill from Target customer (ICP & sources); the user can still change anything for this one search
  useEffect(() => {
    getTarget().then(t => setForm(f => ({
      ...f, titles: t.titles || f.titles, industries: t.industries || f.industries, ranges: t.employee_ranges || f.ranges,
      locations: t.locations || f.locations, jobTitleKeywords: t.job_title_keywords || f.jobTitleKeywords,
    }))).catch(() => {});
  }, []);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const run = async () => {
    if (!apolloConfigured) {
      setError(`${providerLabel} isn't connected yet. Add its key in Settings.`);
      return;
    }
    setBusy(true); setError(""); setResult(null);
    try {
      const payload = {
        titles: csv(form.titles),
        industries: csv(form.industries),
        employee_ranges: form.ranges.split(";").map((x) => x.trim()).filter(Boolean),
        locations: form.locations.split(";").map((x) => x.trim()).filter(Boolean),
        territory_keywords: csv(form.territory),
        page: 1,
        per_page: 25,
        job_title_keywords: csv(form.jobTitleKeywords),
        job_posted_within_days: form.jobPostedWithinDays ? Number(form.jobPostedWithinDays) : null,
        visitor_domains: csv(form.visitorDomains),
        visitor_intent_levels: csv(form.visitorIntentLevels),
        visitor_days: form.visitorDays ? Number(form.visitorDays) : null,
      };
      const data = await apolloSearch(payload);
      setResult(data);
      toast.success(`${data.imported} new prospects · ${data.duplicates_skipped} duplicates · ${data.company_capped || 0} held back (2-per-company cap)`);
      onImported?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : JSON.stringify(detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" data-testid="apollo-modal">
      <div className="modal modal-wide">
        <button className="modal-close" onClick={close} data-testid="close-apollo-modal"><X size={18} /></button>
        <div className="modal-icon"><Zap size={20} /></div>
        <h2>Discover prospects with {providerLabel}</h2>
        <p>Live search through {providerLabel} using your ICP. Change the data provider any time in Settings. Credits are only used by your provider's own pricing.</p>

        {!apolloConfigured && (
          <div className="alert-strip" data-testid="apollo-warning">
            <AlertCircle size={15} />
            <span>{providerLabel} isn't connected. Add its key in <b>Settings</b>.</span>
          </div>
        )}

        <div className="apollo-form">
          <label>Buyer titles<input value={form.titles} onChange={update("titles")} data-testid="apollo-titles" /></label>
          <label>Industry keywords<input value={form.industries} onChange={update("industries")} data-testid="apollo-industries" /></label>
          <label>Employee ranges (semicolon separated, e.g. "51,200")<input value={form.ranges} onChange={update("ranges")} data-testid="apollo-ranges" /></label>
          <label>Person locations (semicolon separated)<input value={form.locations} onChange={update("locations")} data-testid="apollo-locations" /></label>
          <label>Territory keywords (for scoring)<input value={form.territory} onChange={update("territory")} data-testid="apollo-territory" /></label>
        </div>

        <div className="modal-note" style={{ marginTop: 8 }}><Sparkles size={13} /> Hiring signal — narrows results to companies with matching job posts. Clear the field to skip it.</div>
        <div className="apollo-form">
          <label>Job title keywords<input value={form.jobTitleKeywords} onChange={update("jobTitleKeywords")} placeholder="e.g. sales manager, recruiter" data-testid="apollo-job-titles" /></label>
          <label>Posted within (days)<input value={form.jobPostedWithinDays} onChange={update("jobPostedWithinDays")} data-testid="apollo-job-days" /></label>
        </div>

        <div className="modal-note" style={{ marginTop: 8 }}><Sparkles size={13} /> Website-visitor signal — requires Apollo's tracking script installed on your own domain. Leave the domain blank to skip it.</div>
        <div className="apollo-form">
          <label>Your tracked domain<input value={form.visitorDomains} onChange={update("visitorDomains")} placeholder="yourcompany.com" data-testid="apollo-visitor-domains" /></label>
          <label>Intent level<input value={form.visitorIntentLevels} onChange={update("visitorIntentLevels")} data-testid="apollo-visitor-intent" /></label>
          <label>Within (days)<input value={form.visitorDays} onChange={update("visitorDays")} data-testid="apollo-visitor-days" /></label>
        </div>

        {error && <div className="error-strip" data-testid="apollo-error"><AlertCircle size={14} /> {error}</div>}

        {result && (
          <div className="apollo-result" data-testid="apollo-result">
            {(result.notes || []).map(n => <div key={n} className="alert-strip" data-testid="apollo-note"><Sparkles size={13} /> {n}</div>)}
            <div className="apollo-stats">
              <span><b>{result.imported}</b><small>Imported</small></span>
              <span><b>{result.duplicates_skipped}</b><small>Duplicates</small></span>
              <span><b>{result.company_capped || 0}</b><small>Held back (2/company)</small></span>
              <span><b>{result.total_returned}</b><small>{providerLabel} hits</small></span>
            </div>
            <div className="apollo-list">
              {result.prospects.slice(0, 6).map((p) => (
                <div key={p.id} className="apollo-row" data-testid={`apollo-hit-${p.id}`}>
                  <div className="apollo-avatar">{p.initials}</div>
                  <div className="apollo-info">
                    <b>{p.name}</b>
                    <small>{p.role} · {p.company}</small>
                    <small className="muted">{p.location} · Score {p.score} · Tier {p.tier}</small>
                  </div>
                  <span className={`email-pill email-${p.email_status}`}>
                    {p.email_status === "verified" ? <Check size={11} /> : <Sparkles size={11} />}
                    {p.email_status}
                  </span>
                </div>
              ))}
              {result.prospects.length > 6 && <div className="apollo-more">+{result.prospects.length - 6} more added to the queue</div>}
            </div>
          </div>
        )}

        <div className="modal-actions">
          <button className="outline-button" onClick={close} data-testid="apollo-cancel">Close</button>
          <button className="primary-button" onClick={run} disabled={busy} data-testid="apollo-run">
            <Search size={15} /> {busy ? `Searching ${providerLabel}…` : "Run discovery"}
          </button>
        </div>
      </div>
    </div>
  );
}
